/**
 * Dropping a step from the Steps panel into a link, between the two steps it
 * joins. While a step is dragged every link shows a marker; only a drop on a
 * marker inserts, so a step let go near a link by chance is placed as usual.
 * The new step sits below the link's source, lined up with its target, and
 * the target with everything after it moves down to make room.
 *
 * @module components/workflows/canvas/insertion
 */
import type { WorkflowEdge, WorkflowGraph, WorkflowNodeType } from "@amoreg/contracts";

import { actionPoint, hasInput, NODE_GAP, NODE_HEIGHT, nodeHeight, type Point, portsOf, routeEdges } from "./geometry";

/** How close (in screen pixels) the pointer must be to a link's marker to insert there. */
export const INSERT_RADIUS = 22;

/** A step can go between two others when it takes a link in and sends one on (not Start, not Output). */
export const canInsert = (kind: WorkflowNodeType) => hasInput(kind) && portsOf(kind).length > 0;

export interface InsertTarget {
  readonly edge: WorkflowEdge;
  /** Where the marker sits, in chart coordinates. */
  readonly point: Point;
}

/** The link whose marker is under `world` (within `INSERT_RADIUS` on screen at `zoom`), the nearest if several. */
export function insertTargetAt(
  graph: Pick<WorkflowGraph, "nodes" | "edges">,
  sizes: Readonly<Record<string, number>>,
  kind: WorkflowNodeType,
  world: Point,
  zoom: number,
): InsertTarget | null {
  if (!canInsert(kind)) return null;
  let best: InsertTarget | null = null;
  let bestDistance = INSERT_RADIUS;
  for (const routed of routeEdges(graph, sizes)) {
    const point = actionPoint(routed.points);
    const distance = Math.hypot(world.x - point.x, world.y - point.y) * zoom;
    if (distance <= bestDistance) {
      best = { edge: routed.edge, point };
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Where a step dropped into `edge` goes (below the source, lined up with the
 * target) and how far the target and what follows it must move down.
 */
export function insertPlacement(
  graph: Pick<WorkflowGraph, "nodes">,
  sizes: Readonly<Record<string, number>>,
  edge: Pick<WorkflowEdge, "source" | "target">,
): { readonly at: Point; readonly push: number } | null {
  const source = graph.nodes.find((node) => node.id === edge.source);
  const target = graph.nodes.find((node) => node.id === edge.target);
  if (!source || !target) return null;
  const at = { x: target.position.x, y: source.position.y + nodeHeight(source, sizes) + NODE_GAP };
  const push = Math.max(0, Math.round(at.y + NODE_HEIGHT + NODE_GAP - target.position.y));
  return { at, push };
}
